import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Pencil, Trash2, Plus, Search, ChevronLeft, ChevronRight, 
  User, Mail, Phone, Building2, Briefcase, MapPin, 
  IndianRupee, Calendar, Eye, X, Save, Loader2, RotateCcw
} from "lucide-react";

const LeadManager = () => {
  // --- STATES ---
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // --- MODAL STATES ---
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // --- FORM STATE ---
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "", email: "", phoneNumber: "", company: "", salary: "", jobTitle: "", address: ""
  });

  // --- API CALLS ---
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.LEAD_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setLeads(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error(e);
      setLeads([]);
    }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // --- HANDLERS ---
  const handleSearchTrigger = () => { setSearchTerm(inputValue); setCurrentPage(1); };
  const handleClearSearch = () => { setInputValue(""); setSearchTerm(""); setCurrentPage(1); };

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({ fullName: "", email: "", phoneNumber: "", company: "", salary: "", jobTitle: "", address: "" });
    setIsFormModalOpen(true);
  };

  const handleOpenEdit = (l) => {
    setEditingId(l.leadId);
    setFormData({ ...l });
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editingId ? "PUT" : "POST";
      const url = editingId 
        ? `${APIConfig.APIACTIVEURL}${APIConfig.LEAD_BASE}/${editingId}` 
        : `${APIConfig.APIACTIVEURL}${APIConfig.LEAD_BASE}`;

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, leadId: editingId }),
      });

      if (res.ok) {
        setIsFormModalOpen(false);
        fetchLeads();
        Swal.fire("Success", "Lead saved successfully", "success");
      } else {
        Swal.fire("Error", "Failed to save lead", "error");
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete Lead?', text: "This record will be removed permanently.", icon: 'warning', showCancelButton: true });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.LEAD_BASE}/${id}`, { method: "DELETE" });
      fetchLeads();
      Swal.fire("Deleted", "Lead has been removed.", "success");
    }
  };

  // --- STYLES ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" },
    title: { fontSize: "30px", fontWeight: "900", color: "#1b2559", margin: 0 },
    card: { backgroundColor: "#fff", borderRadius: "24px", border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" },
    th: { padding: "18px 24px", backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", textAlign: "left" },
    td: { padding: "18px 24px", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { backgroundColor: "#fff", borderRadius: "28px", width: "600px", padding: "40px", position: "relative", boxShadow: "0 25px 50px rgba(0,0,0,0.15)" },
    input: { width: "100%", padding: "14px 16px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", boxSizing: "border-box" },
    label: { display: "block", fontSize: "11px", fontWeight: "800", color: "#a3aed0", marginBottom: "6px", textTransform: "uppercase" },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", padding: "12px 24px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    btnGhost: { backgroundColor: "#fff", color: "#64748b", padding: "12px 20px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer" }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Leads</h1>
          
          </div>
          <button style={styles.btnPrimary} onClick={handleOpenCreate}><Plus size={20}/> New Lead</button>
        </div>

        {/* Search */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "30px" }}>
          <div style={{ position: "relative", flex: 1, maxWidth: "450px" }}>
            <Search size={18} style={{ position: "absolute", left: "15px", top: "15px", color: "#cbd5e1" }} />
            <input style={{ ...styles.input, paddingLeft: "45px" }} placeholder="Search name, email or company..." value={inputValue} 
              onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchTrigger()} />
          </div>
          <button style={styles.btnPrimary} onClick={handleSearchTrigger}>Search</button>
          {searchTerm && <button style={styles.btnGhost} onClick={handleClearSearch}><RotateCcw size={18} style={{marginRight: '8px'}}/> Reset</button>}
        </div>

        {/* Table */}
        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={styles.th}>Full Name</th>
                <th style={styles.th}>Contact Info</th>
                <th style={styles.th}>Company / Job</th>
                <th style={styles.th}>Salary</th>
                <th style={{ ...styles.th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ textAlign: "center", padding: "80px" }}><Loader2 className="animate-spin" /></td></tr>
              ) : (leads.map(l => (
                <tr key={l.leadId}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: "800", color: "#1b2559", fontSize: "15px" }}>{l.fullName}</div>
                    <div style={{ fontSize: "11px", color: "#4f46e5", fontWeight: "700", marginTop: '2px' }}>
                      <Calendar size={10} style={{marginRight: '4px'}}/>  {new Date(l.createdDate).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#1b2559" }}>{l.phoneNumber}</div>
                    <div style={{ fontSize: "12px", color: "#a3aed0" }}>{l.email}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: "700", color: "#64748b" }}>{l.company}</div>
                    <div style={{ fontSize: "12px", color: "#a3aed0" }}>{l.jobTitle}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", fontWeight: "800", color: "#16a34a" }}>
                       <IndianRupee size={14}/> {l.salary}
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                      <button onClick={() => { setViewData(l); setIsViewModalOpen(true); }} style={{ border: "none", background: "#f1f5f9", padding: "10px", borderRadius: "10px", cursor: "pointer" }}><Eye size={16}/></button>
                      <button onClick={() => handleOpenEdit(l)} style={{ border: "none", background: "#eef2ff", color: "#4f46e5", padding: "10px", borderRadius: "10px", cursor: "pointer" }}><Pencil size={16}/></button>
                      <button onClick={() => handleDelete(l.leadId)} style={{ border: "none", background: "#fef2f2", color: "#ef4444", padding: "10px", borderRadius: "10px", cursor: "pointer" }}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
          <div style={{ padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", color: "#a3aed0", fontWeight: "700" }}>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnGhost}><ChevronLeft size={18}/></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnGhost}><ChevronRight size={18}/></button>
            </div>
          </div>
        </div>

        {/* --- ADD/EDIT MODAL --- */}
        {isFormModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "900", color: "#1b2559" }}>{editingId ? "Update Lead" : "Register New Lead"}</h3>
                <button onClick={() => setIsFormModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1" }}><X size={24}/></button>
              </div>
              
              <form onSubmit={handleFormSubmit}>
                <div style={{ marginBottom: "20px" }}>
                  <label style={styles.label}>Full Name *</label>
                  <div style={{position:'relative'}}>
                    <User size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                    <input style={{...styles.input, paddingLeft:'45px'}} required value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                   <div>
                      <label style={styles.label}>Email Address</label>
                      <div style={{position:'relative'}}>
                        <Mail size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                        <input type="email" style={{...styles.input, paddingLeft:'45px'}} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                      </div>
                   </div>
                   <div>
                      <label style={styles.label}>Phone Number *</label>
                      <div style={{position:'relative'}}>
                        <Phone size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                        <input style={{...styles.input, paddingLeft:'45px'}} required value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} />
                      </div>
                   </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                   <div>
                      <label style={styles.label}>Company Name</label>
                      <div style={{position:'relative'}}>
                        <Building2 size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                        <input style={{...styles.input, paddingLeft:'45px'}} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                      </div>
                   </div>
                   <div>
                      <label style={styles.label}>Job Title</label>
                      <div style={{position:'relative'}}>
                        <Briefcase size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                        <input style={{...styles.input, paddingLeft:'45px'}} value={formData.jobTitle} onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })} />
                      </div>
                   </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                   <label style={styles.label}>Salary (Monthly/Annual)</label>
                   <div style={{position:'relative'}}>
                      <IndianRupee size={16} style={{position:'absolute', left:'15px', top:'15px', color:'#cbd5e1'}}/>
                      <input style={{...styles.input, paddingLeft:'45px'}} value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: e.target.value })} />
                   </div>
                </div>

                <div style={{ marginBottom: "30px" }}>
                    <label style={styles.label}>Current Address</label>
                    <textarea style={{ ...styles.input, height: "80px", resize: "none" }} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px", borderTop: "1px solid #f1f5f9", paddingTop: "30px" }}>
                  <button type="button" onClick={() => setIsFormModalOpen(false)} style={styles.btnGhost}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, padding: "12px 40px" }}>
                    {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} {saving ? "Saving..." : "Save Lead"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- VIEW MODAL --- */}
        {isViewModalOpen && viewData && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "900", color: "#1b2559" }}>Lead Summary</h3>
                <button onClick={() => setIsViewModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1" }}><X size={24}/></button>
              </div>
              <div style={{ display: "grid", gap: "20px" }}>
                 <div style={{ padding: "20px", background: "#f8fafc", borderRadius: "16px", display: "flex", alignItems: "center", gap: "20px" }}>
                    <div style={{ width: "60px", height: "60px", background: "#fff", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", border: "1px solid #e2e8f0" }}><User size={28} color="#4f46e5" /></div>
                    <div>
                       <div style={{ fontWeight: "900", color: "#1b2559", fontSize: '18px' }}>{viewData.fullName}</div>
                       <div style={{ fontSize: "13px", color: "#a3aed0", fontWeight: '700' }}>Inquiry Date: {new Date(viewData.createdDate).toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric'})}</div>
                    </div>
                 </div>
                 <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                    <div style={{ padding: "15px", border: "1.5px solid #f1f5f9", borderRadius: "15px" }}>
                       <div style={styles.label}>Email Address</div>
                       <div style={{ fontWeight: "700", color: "#1b2559", fontSize: '13px' }}>{viewData.email || 'N/A'}</div>
                    </div>
                    <div style={{ padding: "15px", border: "1.5px solid #f1f5f9", borderRadius: "15px" }}>
                       <div style={styles.label}>Phone Number</div>
                       <div style={{ fontWeight: "800", color: "#4f46e5" }}>{viewData.phoneNumber}</div>
                    </div>
                 </div>
                 <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                    <div style={{ padding: "15px", border: "1.5px solid #f1f5f9", borderRadius: "15px" }}>
                       <div style={styles.label}>Company</div>
                       <div style={{ fontWeight: "700", color: "#1b2559" }}>{viewData.company || 'N/A'}</div>
                    </div>
                    <div style={{ padding: "15px", border: "1.5px solid #f1f5f9", borderRadius: "15px" }}>
                       <div style={styles.label}>Salary</div>
                       <div style={{ fontWeight: "700", color: "#16a34a" }}>{viewData.salary || 'N/A'}</div>
                    </div>
                 </div>
                 <div style={{ padding: "15px", border: "1.5px solid #f1f5f9", borderRadius: "15px" }}>
                    <div style={styles.label}>Full Address</div>
                    <div style={{ fontWeight: "600", color: "#1b2559", fontSize:'13px' }}><MapPin size={12} style={{marginRight:'5px'}}/>{viewData.address || 'N/A'}</div>
                 </div>
              </div>
              <button onClick={() => setIsViewModalOpen(false)} style={{ width: "100%", marginTop: "30px", backgroundColor: "#1b2559", color: "#fff", padding: "15px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer" }}>Close Details</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeadManager;